import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  Row,
  Col,
  Typography,
  Space,
  Button,
  Spin,
  Descriptions,
  message,
  Grid,
  Tag,
  Alert
} from "antd";
import {
  EditOutlined,
  ArrowLeftOutlined,
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  HomeOutlined,
  IdcardOutlined,
  SafetyCertificateOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  FileTextOutlined,
  DownloadOutlined
} from "@ant-design/icons";
import { getCustomerById, deleteCustomer, restoreCustomer } from "../api";
import { useDeleteConfirm } from "../../../components/DeleteConfirm";
import {
  useCustomerEdit, CustomerEditActions, CustomerEditBanner, CustomerEditHistory,
} from "../components/CustomerEditControl";
import { usePermissions } from "../../../hooks/usePermissions";
import { ModuleType } from "components";
import DigilockerModal from "../components/DigilockerModal";
import CustomerDocumentsSection from "../components/CustomerDocumentsSection";
import CustomerBankSection from "../components/CustomerBankSection";
import CustomerCasesSection from "../components/CustomerCasesSection";
import "bootstrap/dist/css/bootstrap.min.css";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const CustomerDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const { canViewModule, canViewField, canEditModule } = usePermissions();
  const module = ModuleType.CUSTOMER;

  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isKycModalVisible, setIsKycModalVisible] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const { confirmPermanentDelete, confirmRestore, contextHolder: deleteModalHolder, isAdmin } = useDeleteConfirm();
  // Approval-gated editing: non-admins request fields → approval → one save.
  const { state: editState, reload: reloadEditState } = useCustomerEdit(id);

  const handleRestore = async () => {
    try {
      await restoreCustomer(id);
      messageApi.success("Customer restored");
      await loadCustomer();
    } catch (err) {
      messageApi.error(err?.response?.data?.message || "Failed to restore customer");
    }
  };

  const handlePermanentDelete = async () => {
    try {
      await deleteCustomer(id, { permanent: true });
      messageApi.success("Customer permanently deleted");
      setTimeout(() => navigate("/customers"), 500);
    } catch (err) {
      messageApi.error(err?.response?.data?.message || "Failed to delete customer");
    }
  };

  useEffect(() => {
    loadCustomer();
  }, [id]);

  const loadCustomer = async () => {
    setLoading(true);
    try {
      const response = await getCustomerById(id);
      setCustomer(response.data);
    } catch (error) {
      messageApi.error("Failed to load customer details");
      navigate("/customers");
    } finally {
      setLoading(false);
    }
  };

  // Refetch without the full-page spinner (after a document / bank save).
  const refreshCustomer = async () => {
    try {
      const response = await getCustomerById(id);
      setCustomer(response.data);
    } catch {
      messageApi.error("Failed to refresh customer details");
    }
  };

  const getKycStatusBadge = () => {
    if (!customer) return null;
    switch (customer.kycStatus) {
      case 'verified':
        return <Tag color="success" icon={<CheckCircleOutlined />}>KYC Verified via {customer.kycProvider}</Tag>;
      case 'pending':
        return <Tag color="warning" icon={<SyncOutlined spin />}>KYC Pending</Tag>;
      case 'failed':
        return <Tag color="error" icon={<CloseCircleOutlined />}>KYC Failed</Tag>;
      default:
        return <Tag color="default">KYC Not Initiated</Tag>;
    }
  };
  // added new
  if (!canViewModule(ModuleType.CUSTOMER)) {
    return (
      <div className="text-center mt-5">
        <Text type="danger">
          You do not have permission to view customer details.
        </Text>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: 400 }}
      >
        <Spin size="large" />
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="text-center mt-5">
        <Text>Customer not found.</Text>
      </div>
    );
  }

  return (
    <>
      {contextHolder}
      {deleteModalHolder}
      <main className="container-fluid px-2 px-md-4 py-3">
        {customer?.isDeleted && (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            message="This customer has been deleted"
            description={`Deleted${customer.deletedBy?.name ? ` by ${customer.deletedBy.name}` : ""}${customer.deletedAt ? ` on ${new Date(customer.deletedAt).toLocaleString("en-IN")}` : ""}. They are hidden from all lists.`}
            action={isAdmin && (
              <Space direction="vertical">
                <Button size="small" onClick={() => confirmRestore({ what: `customer ${customer.name}`, onRestore: handleRestore })}>Restore</Button>
                <Button size="small" danger onClick={() => confirmPermanentDelete({ what: `customer ${customer.name}`, onDelete: handlePermanentDelete })}>
                  Delete permanently
                </Button>
              </Space>
            )}
          />
        )}
        <Row gutter={[24, 24]}>
          <Col xs={24}>
            <Card
              bordered={false}
              style={{
                borderRadius: 12,
                boxShadow: "0 2px 12px rgba(22,119,255,0.06)",
              }}
              bodyStyle={{ padding: screens.xs ? 16 : 24 }}
            >
              <Row justify="space-between" align="middle" className="mb-4">
                <Col xs={24} sm={12}>
                  <Space>
                    <Button
                      icon={<ArrowLeftOutlined />}
                      onClick={() => navigate("/customers")}
                      aria-label="Back to Customers"
                    >
                      Back
                    </Button>
                    <Title level={3} className="mb-0">
                      Customer Details
                    </Title>
                    {getKycStatusBadge()}
                  </Space>
                </Col>
                <Col
                  xs={24}
                  sm={12}
                  className="d-flex justify-content-sm-end mt-2 mt-sm-0"
                >
                  <Space>
                    {customer && customer.kycStatus !== 'verified' && (
                      <Button
                        type="primary"
                        icon={<SafetyCertificateOutlined />}
                        onClick={() => setIsKycModalVisible(true)}
                      >
                        Verify via DigiLocker
                      </Button>
                    )}
                    <CustomerEditActions
                      customerId={id}
                      isAdmin={isAdmin}
                      canEdit={canEditModule(module) && !customer?.isDeleted}
                      state={editState}
                      reload={reloadEditState}
                      onEdit={() => navigate(`/customers/edit/${customer._id}`)}
                    />
                  </Space>
                </Col>
              </Row>

              <CustomerEditBanner state={editState} />

              <Row gutter={[24, 24]}>
                <Col xs={24} lg={12}>
                  <Card
                    title={
                      <Space>
                        <UserOutlined />
                        <span>Personal Information</span>
                      </Space>
                    }
                    size="small"
                    style={{ height: "100%" }}
                  >
                    <Descriptions column={1} size="small">
                      {canViewField(module, 'customerId') && (
                        <Descriptions.Item label="Customer ID">
                          <Text strong>{customer.customerId || "-"}</Text>
                        </Descriptions.Item>
                      )}
                      {canViewField(module, 'name') && (
                        <Descriptions.Item label="Name">
                          <Text strong>{customer.name || "-"}</Text>
                        </Descriptions.Item>
                      )}
                      {canViewField(module, 'phone') && (
                        <Descriptions.Item label="Phone">
                          <Space>
                            <PhoneOutlined />
                            <Text>{customer.phone || "-"}</Text>
                          </Space>
                        </Descriptions.Item>
                      )}
                      {canViewField(module, 'alternatePhone') && (
                        <Descriptions.Item label="Alternate Phone">
                          <Space>
                            <PhoneOutlined />
                            <Text>{customer.alternatePhone || "-"}</Text>
                          </Space>
                        </Descriptions.Item>
                      )}
                      {canViewField(module, 'email') && (
                        <Descriptions.Item label="Email">
                          <Space>
                            <MailOutlined />
                            <Text>{customer.email || "-"}</Text>
                          </Space>
                        </Descriptions.Item>
                      )}
                      {canViewField(module, 'dateOfBirth') && (
                        <Descriptions.Item label="Date of Birth">
                          <Text>
                            {customer.dateOfBirth
                              ? new Date(customer.dateOfBirth).toLocaleDateString()
                              : "-"}
                          </Text>
                        </Descriptions.Item>
                      )}
                      {canViewField(module, 'gender') && (
                        <Descriptions.Item label="Gender">
                          <Text>{customer.gender || "-"}</Text>
                        </Descriptions.Item>
                      )}
                      <Descriptions.Item label="Created By">
                        <Text>{customer.createdBy?.name || "-"}</Text>
                      </Descriptions.Item>
                      <Descriptions.Item label="Created Date">
                        <Text>
                          {customer.createdAt
                            ? new Date(customer.createdAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })
                            : "-"}
                        </Text>
                      </Descriptions.Item>
                    </Descriptions>
                  </Card>
                </Col>

                <Col xs={24} lg={12}>
                  <Card
                    title={
                      <Space>
                        <UserOutlined />
                        <span>Profile Photo</span>
                      </Space>
                    }
                    size="small"
                    style={{ height: "100%" }}
                  >
                    {canViewField(module, 'photo') && customer.photo ? (
                      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                        <img
                          src={customer.photo}
                          alt="Profile"
                          style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 8 }}
                        />
                      </div>
                    ) : (
                      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px', color: '#999' }}>
                        No photo available
                      </div>
                    )}
                  </Card>
                </Col>

                <Col xs={24}>
                  <Card
                    title={
                      <Space>
                        <HomeOutlined />
                        <span>Address Information</span>
                      </Space>
                    }
                    size="small"
                  >
                    <Descriptions column={{ xs: 1, md: 2 }} size="small">
                      {canViewField(module, 'address') && (
                        <Descriptions.Item label="Address">
                          <Text>{customer.address || "-"}</Text>
                        </Descriptions.Item>
                      )}
                      {canViewField(module, 'city') && (
                        <Descriptions.Item label="City">
                          <Text>{customer.city || "-"}</Text>
                        </Descriptions.Item>
                      )}
                      {canViewField(module, 'state') && (
                        <Descriptions.Item label="State">
                          <Text>{customer.state || "-"}</Text>
                        </Descriptions.Item>
                      )}
                      {canViewField(module, 'pincode') && (
                        <Descriptions.Item label="PIN Code">
                          <Text>{customer.pincode || "-"}</Text>
                        </Descriptions.Item>
                      )}
                    </Descriptions>
                  </Card>
                </Col>

                <Col xs={24}>
                  <Card
                    title={
                      <Space>
                        <IdcardOutlined />
                        <span>Values & KYC</span>
                      </Space>
                    }
                    size="small"
                  >
                    <Descriptions column={{ xs: 1, md: 2 }} size="small">
                      {canViewField(module, 'aadhaar') && (
                        <Descriptions.Item label="Aadhaar Number">
                          <Text>{customer.aadhaar || "-"}</Text>
                        </Descriptions.Item>
                      )}
                      {canViewField(module, 'pan') && (
                        <Descriptions.Item label="PAN Number">
                          <Text>{customer.pan || "-"}</Text>
                        </Descriptions.Item>
                      )}


                      {customer.kycStatus === 'verified' && customer.kycData && (
                        <>
                          <Descriptions.Item label="Photo" span={2}>
                            {customer.kycData.photo ? (
                              <img
                                src={customer.kycData.photo.startsWith('data:image') ? customer.kycData.photo : `data:image/jpeg;base64,${customer.kycData.photo}`}
                                alt="KYC Photo"
                                style={{ width: 100, borderRadius: 8, border: '1px solid #d9d9d9' }}
                              />
                            ) : <Text type="secondary">Not Available</Text>}
                          </Descriptions.Item>
                          <Descriptions.Item label="Name"><Text type="secondary">{customer.kycData.name}</Text></Descriptions.Item>
                          <Descriptions.Item label="Care Of"><Text type="secondary">{customer.kycData.care_of || "-"}</Text></Descriptions.Item>
                          <Descriptions.Item label="Gender"><Text type="secondary">{customer.kycData.gender}</Text></Descriptions.Item>
                          <Descriptions.Item label="DOB"><Text type="secondary">{customer.kycData.date_of_birth || customer.kycData.dob || "-"}</Text></Descriptions.Item>
                          <Descriptions.Item label="Address" span={2}><Text type="secondary">{customer.kycData.full_address || "-"}</Text></Descriptions.Item>
                          <Descriptions.Item label="Reference ID"><Text type="secondary">{customer.kycData.reference_id}</Text></Descriptions.Item>
                          <Descriptions.Item label="Transaction ID"><Text type="secondary">{customer.kycData.transaction_id || "-"}</Text></Descriptions.Item>
                        </>
                      )}
                    </Descriptions>
                  </Card>
                </Col>

                {Array.isArray(customer.kycDocuments) && customer.kycDocuments.length > 0 && (
                  <Col xs={24}>
                    <Card
                      title={
                        <Space>
                          <FileTextOutlined />
                          <span>Documents Fetched</span>
                          <Tag color="blue">{customer.kycDocuments.length}</Tag>
                        </Space>
                      }
                      size="small"
                    >
                      <Row gutter={[16, 16]}>
                        {customer.kycDocuments.map((doc, idx) => {
                          const parsed = doc.parsed || {};
                          return (
                            <Col xs={24} md={12} key={doc.fileId || doc.url || idx}>
                              <Card
                                size="small"
                                type="inner"
                                title={
                                  <Space wrap>
                                    <Tag color={doc.docType === 'aadhaar' ? 'green' : 'geekblue'}>
                                      {(doc.docType || 'document').toUpperCase()}
                                    </Tag>
                                    <Text strong>{doc.description || doc.issuer || 'Document'}</Text>
                                  </Space>
                                }
                                extra={
                                  doc.url ? (
                                    <Button
                                      type="link"
                                      size="small"
                                      icon={<DownloadOutlined />}
                                      href={doc.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      View
                                    </Button>
                                  ) : null
                                }
                              >
                                <Descriptions column={1} size="small">
                                  {doc.issuer && (
                                    <Descriptions.Item label="Issuer">
                                      <Text type="secondary">{doc.issuer}</Text>
                                    </Descriptions.Item>
                                  )}
                                  {doc.fetchedAt && (
                                    <Descriptions.Item label="Fetched">
                                      <Text type="secondary">
                                        {new Date(doc.fetchedAt).toLocaleString()}
                                      </Text>
                                    </Descriptions.Item>
                                  )}
                                  {parsed.name && (
                                    <Descriptions.Item label="Name">
                                      <Text type="secondary">{parsed.name}</Text>
                                    </Descriptions.Item>
                                  )}
                                  {parsed.dob && (
                                    <Descriptions.Item label="DOB">
                                      <Text type="secondary">{parsed.dob}</Text>
                                    </Descriptions.Item>
                                  )}
                                  {parsed.gender && (
                                    <Descriptions.Item label="Gender">
                                      <Text type="secondary">{parsed.gender}</Text>
                                    </Descriptions.Item>
                                  )}
                                  {parsed.aadhaar && (
                                    <Descriptions.Item label="Aadhaar">
                                      <Text type="secondary">{parsed.aadhaar}</Text>
                                    </Descriptions.Item>
                                  )}
                                  {parsed.full_address && (
                                    <Descriptions.Item label="Address">
                                      <Text type="secondary">{parsed.full_address}</Text>
                                    </Descriptions.Item>
                                  )}
                                </Descriptions>
                              </Card>
                            </Col>
                          );
                        })}
                      </Row>
                    </Card>
                  </Col>
                )}

                <Col xs={24}>
                  <CustomerDocumentsSection
                    customerId={id}
                    documents={customer.documents || []}
                    canUpload={canEditModule(module) && !customer.isDeleted}
                    isAdmin={isAdmin}
                    onChanged={refreshCustomer}
                  />
                </Col>

                <Col xs={24}>
                  <CustomerBankSection
                    customer={customer}
                    canEdit={canEditModule(module) && !customer.isDeleted}
                    isAdmin={isAdmin}
                    editState={editState}
                    reloadEditState={reloadEditState}
                    onChanged={refreshCustomer}
                  />
                </Col>

                <Col xs={24}>
                  <CustomerCasesSection customerId={id} />
                </Col>

                <Col xs={24}>
                  <Card
                    title={<Space><EditOutlined /><span>Edit History</span></Space>}
                    size="small"
                    style={{ borderRadius: 8 }}
                  >
                    <CustomerEditHistory state={editState} />
                  </Card>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      </main >

      {customer && (
        <DigilockerModal
          visible={isKycModalVisible}
          onClose={() => setIsKycModalVisible(false)}
          customerId={customer._id}
          mode="linked"
          onSuccess={() => {
            loadCustomer(); // The server already saved the KYC; reload to show it
          }}
        />
      )
      }
    </>
  );
};

export default CustomerDetails;
